import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, TriangleAlert } from "lucide-react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { ORDER_STATUS_LABELS } from "../../order-list/services/order-service";
import TransportVehicleService, {
  TRANSPORT_VEHICLE_STATUS,
  TransportVehicle,
  VehicleTransferPreview,
} from "../services/transport-vehicle-service";

interface Props {
  /** Xe nguồn (VD xe hỏng) — đang có đội hình. */
  source: TransportVehicle;
  onClose: () => void;
  onDone: () => void;
}

const LEG_LABEL: Record<string, string> = { pickup: "Lấy hàng", delivery: "Giao hàng" };

/**
 * CHUYỂN CẢ ĐỘI HÌNH (tài xế + phụ xe) sang xe khác — dùng khi xe hỏng phải đổi xe. Chọn xe đích (Hoạt động, trống
 * đội hình) → xem trước đơn / chuyến đang dùng xe cũ sẽ đi theo → tick xác nhận → chuyển.
 * Người và phân công giữ nguyên (kể cả đơn tài xế đã nhận), chỉ đổi xe. Xe cũ giữ Hoạt động, không còn đội hình.
 */
const TransportVehicleTransferModal: React.FC<Props> = ({ source, onClose, onDone }) => {
  const { fetch: listVehicles, loading: loadingVehicles } = TransportVehicleService.list();
  const { fetch: fetchPreview, loading: loadingPreview } = TransportVehicleService.transferPreview();
  const { fetch: doTransfer, loading: transferring } = TransportVehicleService.transfer();

  const [vehicles, setVehicles] = useState<TransportVehicle[]>([]);
  const [targetId, setTargetId] = useState<number | "">("");
  const [preview, setPreview] = useState<VehicleTransferPreview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const listRef = useRef(listVehicles);
  const previewRef = useRef(fetchPreview);
  listRef.current = listVehicles;
  previewRef.current = fetchPreview;

  // Nạp TOÀN BỘ xe (không theo bộ lọc của trang) để chọn xe đích.
  useEffect(() => {
    void (async () => {
      const res = await listRef.current({});
      if (res && isSuccess(res)) setVehicles((res.data as TransportVehicle[]) || []);
    })();
  }, []);

  // Xe đích hợp lệ: Hoạt động, trống đội hình, không đang chở hàng.
  const candidates = useMemo(
    () =>
      vehicles.filter(
        (v) =>
          v.id !== source.id &&
          v.status === TRANSPORT_VEHICLE_STATUS.ACTIVE &&
          !v.driver_id &&
          !v.assistant_id &&
          !v.is_in_transit
      ),
    [vehicles, source.id]
  );

  useEffect(() => {
    setPreview(null);
    setPreviewError("");
    setConfirmed(false);
    if (!targetId) return;
    void (async () => {
      const res = await previewRef.current(source.id, Number(targetId));
      if (res && isSuccess(res)) setPreview(res.data as VehicleTransferPreview);
      else setPreviewError(res?.message || "Không xem trước được việc chuyển đội hình");
    })();
  }, [targetId, source.id]);

  const startedCount = preview?.orders.filter((o) => o.legs.some((l) => l.started)).length ?? 0;
  const onBoardCount = preview?.open_trips.reduce((s, t) => s + t.on_board_count, 0) ?? 0;

  const submit = async () => {
    if (!targetId || !preview || !confirmed) return;
    const res = await doTransfer(source.id, Number(targetId));
    if (res && isSuccess(res)) {
      showToast({
        message: `Đã chuyển đội hình sang xe ${preview.to.plate_number}${preview.orders.length ? ` · ${preview.orders.length} đơn đi theo` : ""}`,
        type: "success",
      });
      onDone();
      return;
    }
    showToast({ message: res?.message ?? "Không chuyển được đội hình", type: "error" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-slate-800">Chuyển đội hình sang xe khác</h3>
          <p className="mt-1 text-xs text-slate-500">
            Dùng khi xe hỏng / cần đổi xe. Tài xế + phụ xe và mọi đơn, chuyến chưa xong của xe cũ chuyển sang xe mới —
            không cần phân công lại, kể cả đơn tài xế đã nhận.
          </p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {/* Xe nguồn → xe đích */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Xe hiện tại</label>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <div className="font-medium text-slate-800">{source.plate_number}</div>
                <div className="text-xs text-slate-500">
                  TX: {source.driver_name || `NV #${source.driver_id}`}
                  {source.assistant_id ? ` · PX: ${source.assistant_name || `NV #${source.assistant_id}`}` : ""}
                </div>
              </div>
            </div>
            <ArrowRight className="mb-3 h-5 w-5 text-slate-400" />
            <div>
              <label className="mb-1 block text-xs text-slate-500">
                Xe đích <span className="text-red-500">*</span>
              </label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : "")}
                disabled={loadingVehicles || transferring}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">{loadingVehicles ? "Đang tải..." : "— Chọn xe trống đội hình —"}</option>
                {candidates.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.plate_number} · {v.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {!loadingVehicles && candidates.length === 0 && (
            <p className="text-xs text-amber-600">
              Không có xe nào đang Hoạt động và trống đội hình. Thêm xe mới hoặc gỡ đội hình của một xe khác trước.
            </p>
          )}

          {loadingPreview && <div className="text-sm text-slate-500">Đang kiểm tra các đơn đang dùng xe...</div>}
          {previewError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{previewError}</div>
          )}

          {preview && (
            <>
              <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Đội hình <b>{preview.crew.driver_name}</b>
                  {preview.crew.assistant_name ? (
                    <>
                      {" "}+ <b>{preview.crew.assistant_name}</b> ({preview.crew.driver_ratio}/
                      {Math.round((100 - preview.crew.driver_ratio) * 100) / 100})
                    </>
                  ) : null}{" "}
                  sẽ chuyển từ xe <b>{preview.from.plate_number}</b> sang xe <b>{preview.to.plate_number}</b>.
                  <ul className="mt-1 list-disc pl-5 text-xs">
                    <li>
                      <b>{preview.orders.length}</b> đơn đang dùng xe cũ đổi sang xe mới
                      {startedCount > 0 && <> (trong đó <b>{startedCount}</b> đơn tài xế đã nhận / đang chạy)</>}.
                    </li>
                    {preview.open_trips.length > 0 && (
                      <li>
                        <b>{preview.open_trips.length}</b> chuyến trung chuyển đang mở ({onBoardCount} đơn trên xe) chuyển
                        sang xe mới.
                      </li>
                    )}
                    {preview.pending_handovers > 0 && (
                      <li>
                        <b>{preview.pending_handovers}</b> mã bàn giao lên xe đang chờ quét chuyển sang xe mới.
                      </li>
                    )}
                    <li>
                      Xe <b>{preview.from.plate_number}</b> vẫn Hoạt động nhưng không còn đội hình (chưa dùng được).
                    </li>
                    <li>Tài xế, phụ xe nhận thông báo; mỗi đơn ghi nhật ký đổi xe.</li>
                  </ul>
                </div>
              </div>

              {preview.orders.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Mã đơn</th>
                        <th className="px-3 py-2 text-left">Trạng thái đơn</th>
                        <th className="px-3 py-2 text-left">Chiều dùng xe</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.orders.map((o) => (
                        <tr key={o.id}>
                          <td className="px-3 py-2 font-medium text-slate-800">#{o.code}</td>
                          <td className="px-3 py-2 text-slate-600">{ORDER_STATUS_LABELS[o.status] || `#${o.status}`}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {o.legs.map((l) => (
                                <span
                                  key={l.leg}
                                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                    l.started ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {LEG_LABEL[l.leg]} · {l.started ? "đang chạy" : "chưa bắt đầu"}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-green-600"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                Tôi đã kiểm tra danh sách trên và xác nhận chuyển đội hình sang xe {preview.to.plate_number}.
              </label>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            disabled={transferring}
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Hủy
          </button>
          <button
            onClick={submit}
            disabled={!preview || !confirmed || transferring}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
          >
            {transferring ? "Đang chuyển..." : "Chuyển đội hình"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransportVehicleTransferModal;
